import React from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { useAppSelector } from '@/store';

import { ROUTE } from '../../routes';
import { useShowInProgress } from '../../hooks/useShowInProgress';
import type { RunningCampaignData } from '../../mock-data/types';
import { suggestedCampaignData } from '../../mock-data/suggested-campaign';
import { runningCampaignData } from '../../mock-data/running-campaign';
import { endedCampaignData } from '../../mock-data/ended-campaign';
import { withRunningCardIdentity, withSuggestedCardIdentity } from '../../utils/card-identity';
import {
  resolveCampaignStatus,
  formatStatusAt,
  useCampaignOverrides,
  useCampaignStatusActions,
} from '../../utils/campaign-status';
import { resolveLocalizedText } from '../acquisition/api/casino-ad-service';
import SuggestedCampaign from '../suggested-campaign';
import RunningCampaign from '../running-campaign';
import { useResolvedCampaign } from './useResolvedCampaign';
import { useCampaignPerformance } from './useCampaignPerformance';
import { mapCampaignAssets } from './campaign-performance';

export type CampaignDetailProps = {
  styleText?: string;
  className?: string;
};

/**
 * Route dispatcher for `account/marketing/campaign/:id`.
 *
 * A campaign detail has two shapes — Suggested and Running — but only one
 * parameterised route. This resolves `:id` to a campaign, reconciles its status
 * with any runtime pause/resume the owner made and the expiry rule (a campaign
 * that once ran and has passed its deadline is treated as ended — see
 * `utils/campaign-status`), then renders the template that matches the *effective*
 * status. Running and paused campaigns share the Running template; ended ones
 * open it in its historical (ended) mode; unknown ids render nothing.
 *
 * A real (API) running/paused campaign is now FULLY wired: the clicked card's identity
 * (title / artwork / period) is stamped on, then every figure is overridden with live
 * data — hero Ad Spend, the performance KPIs and the Top 10 Websites from
 * `useCampaignPerformance` (ad cost + the campaign-filtered Stats conversions), and the
 * creative assets from the campaign's own generated banners. The offering is the
 * campaign's promotion headline (localised to the interface language; `?` when absent)
 * and the subtitle tagline is dropped. Suggested + ended details and
 * a legacy mock-pool campaign still render the shared generic mock with the identity
 * stamped on it (see utils/card-identity). Pause/resume lives in the `campaignStatus`
 * slice.
 */
const CampaignDetail = ({ styleText = '', className = '' }: CampaignDetailProps): React.ReactElement | null => {
  const { t, i18n } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const overrides = useCampaignOverrides();
  const { activate, pause, resume } = useCampaignStatusActions();

  // Resolve `:id` from the mock pool first, then the Casino Ad Service (the
  // acquisition browser now lists real campaigns). While the real lookup is in
  // flight, render nothing rather than flashing the "unknown id" blank.
  // The active brand's tenant id routes the Ad Service proxy calls; its `id` scopes the
  // Stats calls. Both come off the displayed brand.
  const tenantId = useAppSelector((state) => state.casinoInstance.displayBrand?.tenant_id);
  const casinoInstanceId = useAppSelector((state) => state.casinoInstance.displayBrand?.id);
  // The Campaigns browser that links here shows only under the plain "777" reveal, so without it a campaign page
  // (a bookmark, history) bounces to Acquisition, as the Lumio channel pages do, and reads nothing on the way.
  const revealed = useShowInProgress();
  const { campaign, detail, loading } = useResolvedCampaign(revealed ? id : undefined, tenantId);

  // Live figures for a real (API) campaign — the hero Ad Spend, the performance KPIs
  // (ad cost + the campaign-filtered Stats conversions) and the Top 10 Websites. Keyed
  // by the resolved campaign id; idle (and unused) for a mock-pool sample. Hooks run
  // unconditionally, so this sits above the early returns.
  const perf = useCampaignPerformance(
    detail?.campaign_id,
    tenantId,
    casinoInstanceId,
    detail?.start_date,
    runningCampaignData.performance.kpis,
  );

  if (!revealed) return <Navigate to={ROUTE.acquisition()} replace />;
  if (loading) return null;
  if (!campaign) return null;

  const status = resolveCampaignStatus(campaign.status, campaign.expiresAt, overrides[campaign.id]?.status, Date.now());

  if (status === 'suggestion') {
    const data = withSuggestedCardIdentity(suggestedCampaignData, campaign);
    // Activate → flip to running, then drop the owner onto the Acquisition
    // Running tab with this campaign flagged (via location state) for the
    // "just activated" highlight.
    const onActivate = (): void => {
      activate(campaign.id);
      navigate(`${ROUTE.acquisition()}?tab=running`, { state: { activatedId: campaign.id } });
    };
    return <SuggestedCampaign data={data} onActivate={onActivate} styleText={styleText} className={className} />;
  }

  // Ended campaigns open the Running template in its historical (ended) mode —
  // final performance + the creatives/placements the owner analyses. The "Ended
  // on" date is the campaign's own end timestamp; for one that ended by passing
  // its expiry (was running/paused), the expiry deadline is that timestamp.
  if (status === 'ended') {
    const endedOn =
      campaign.status === 'ended'
        ? (campaign.statusAt ?? endedCampaignData.hero.endedOn)
        : campaign.expiresAt != null
          ? formatStatusAt(campaign.expiresAt)
          : (campaign.statusAt ?? endedCampaignData.hero.endedOn);
    const data = withRunningCardIdentity(
      { ...endedCampaignData, hero: { ...endedCampaignData.hero, endedOn } },
      campaign,
    );
    return <RunningCampaign data={data} status="ended" styleText={styleText} className={className} />;
  }

  // Running or paused → the live template. A paused campaign drops the live glow,
  // swaps Pause for a primary Resume button and re-titles the header.
  const base = withRunningCardIdentity(runningCampaignData, campaign);
  const data = status === 'paused' ? { ...base, header: { title: t('Paused Campaign Details') } } : base;

  // Real (API) campaign: override every figure with live data. The hero Ad Spend, the
  // KPI grid and the Top 10 Websites come from `useCampaignPerformance`; the assets are
  // the campaign's own generated banners. The hero Offering is the campaign's promotion
  // headline (`offering.promotion_details.title`), localised to the interface language;
  // the subtitle tagline is dropped. A mock-pool sample (no `detail`) falls through to
  // its static payload + the template's own mock latency.
  if (detail) {
    const assets = mapCampaignAssets(detail.assets, detail.network);
    // The promotion title ships as an `en` / `en-us` / `es-mx` locale bag. Resolve it for
    // the interface language; keep the `?` placeholder when a campaign carries no offering.
    const offeringTitle = resolveLocalizedText(detail.offering?.promotion_details?.title, i18n.language);
    const liveData: RunningCampaignData = {
      ...data,
      hero: {
        ...data.hero,
        subtitle: '',
        offering: { ...data.hero.offering, value: offeringTitle || '?' },
        adSpend: { ...data.hero.adSpend, value: perf.heroAdSpend },
      },
      performance: { kpis: perf.kpis },
      // Always render the Top Websites section for a real campaign (the template shows a
      // skeleton while loading and an empty state when the campaign has no traffic yet),
      // so it reads as wired rather than missing.
      topWebsites: { items: perf.topWebsites },
      assets: assets.length > 0 ? { items: assets, hasMore: false } : undefined,
    };
    return (
      <RunningCampaign
        data={liveData}
        status={status}
        loading={perf.loading}
        onPause={() => pause(campaign.id)}
        onResume={() => resume(campaign.id, { expiresAt: campaign.expiresAt })}
        styleText={styleText}
        className={className}
      />
    );
  }

  return (
    <RunningCampaign
      data={data}
      status={status}
      onPause={() => pause(campaign.id)}
      onResume={() => resume(campaign.id, { expiresAt: campaign.expiresAt })}
      styleText={styleText}
      className={className}
    />
  );
};

export default CampaignDetail;
